// Collapsible cart-card sections (design `Tronshop Cart.dc.html`): variants and printing details.
// Markup contract: a container with data-cart-collapse-section + data-cart-collapse-key +
// data-cart-collapse-default="open|closed", containing [data-cart-collapse-toggle] and
// [data-cart-collapse-body]. State is the `is-collapsed` class on the container (CSS hides the body,
// swaps the toggle labels and shows the collapsed summary line).
//
// State persists per section key in sessionStorage — the cart AJAX-reloads after every designer
// action (save/link/delete), and without persistence every section the customer opened would snap
// back to its default on each reload.

const STORAGE_KEY = "cart_collapse_state";
const COLLAPSED_CLASS = "is-collapsed";

type CollapseState = Record<string, boolean>; // section key -> collapsed

function readState(): CollapseState {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) as CollapseState : {};
    } catch {
        return {};
    }
}

function writeState(state: CollapseState): void {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Persistence is an optimization — without it sections just reopen per their defaults.
    }
}

const ANIMATION_MS = 220;

function prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Animated open/close: the resting states stay CSS-driven (is-collapsed → display:none), the
// transition is a temporary inline height/opacity tween on the body. Reduced-motion users get the
// instant class flip.
function setCollapsed(section: HTMLElement, collapsed: boolean, animate: boolean): void {
    const body = section.querySelector<HTMLElement>("[data-cart-collapse-body]");
    if (!animate || !body || prefersReducedMotion()) {
        section.classList.toggle(COLLAPSED_CLASS, collapsed);
        return;
    }

    body.style.overflow = "hidden";
    body.style.transition = `height ${ANIMATION_MS}ms ease, opacity ${ANIMATION_MS - 40}ms ease`;
    if (collapsed) {
        body.style.height = `${body.scrollHeight}px`;
        window.requestAnimationFrame(() => {
            body.style.height = "0px";
            body.style.opacity = "0";
        });
        window.setTimeout(() => {
            section.classList.add(COLLAPSED_CLASS);
            body.style.cssText = "";
        }, ANIMATION_MS + 20);
    } else {
        section.classList.remove(COLLAPSED_CLASS);
        const target = body.scrollHeight;
        body.style.height = "0px";
        body.style.opacity = "0";
        window.requestAnimationFrame(() => {
            body.style.height = `${target}px`;
            body.style.opacity = "1";
        });
        window.setTimeout(() => {
            body.style.cssText = "";
        }, ANIMATION_MS + 20);
    }
}

export function initCartCollapse(): void {
    document.querySelectorAll<HTMLElement>("[data-cart-collapse-section]").forEach(section => {
        if (section.dataset.cartCollapseBound) return;
        section.dataset.cartCollapseBound = "1";

        const key = section.dataset.cartCollapseKey;
        const stored = key ? readState()[key] : undefined;
        const collapsed = stored !== undefined ? stored : section.dataset.cartCollapseDefault === "closed";
        setCollapsed(section, collapsed, false);

        // Accessibility: tell assistive tech the toggle button controls the details body and its
        // expanded/collapsed state (the visual state was previously conveyed by CSS only).
        const toggleBtn = section.querySelector<HTMLElement>("[data-cart-collapse-toggle]");
        const body = section.querySelector<HTMLElement>("[data-cart-collapse-body]");
        if (toggleBtn && body) {
            if (!body.id) {
                body.id = `cart-collapse-body-${(key ?? "").replace(/[^a-z0-9]+/gi, "-")}`;
            }
            toggleBtn.setAttribute("aria-controls", body.id);
            toggleBtn.setAttribute("aria-expanded", String(!collapsed));
        }

        const toggle = (): void => {
            const nowCollapsed = !section.classList.contains(COLLAPSED_CLASS);
            setCollapsed(section, nowCollapsed, true);
            toggleBtn?.setAttribute("aria-expanded", String(!nowCollapsed));
            if (key) {
                const state = readState();
                state[key] = nowCollapsed;
                writeState(state);
            }
        };

        toggleBtn?.addEventListener("click", toggle);

        // The whole header row is a toggle target — interactive children (buttons, links, uploads)
        // keep their own behaviour. The explicit toggle button is excluded here too: its own handler
        // above already fired for the same click.
        section.querySelector<HTMLElement>("[data-cart-collapse-head]")?.addEventListener("click", evt => {
            if ((evt.target as HTMLElement).closest("button, a, label, input, [data-td-logo-action], [data-tl-action], [data-cart-collapse-ignore]")) {
                return;
            }
            toggle();
        });
    });
}
